import "dayjs/locale/ja";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/types";
import { Button, Form, Table } from "antd";
import { useEffect, useState } from "react";
import { Actions, Cells } from "../../../shared/constants";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import tableUtils from "../../../utils/tableUtils";
import HrnModal from "../../../components/HrnModal";
import validateMessages from "../../../utils/validate";
import { useCreateServiceManMutation, useDeleteServiceManMutation, useLazyGetServiceMenQuery, useUpdateServiceManMutation } from "../../../services/serviceMan";
import ServiceManForm from "./ServiceManForm";
import { useLazyGetRolesQuery } from "../../../services/role";
import { useLazyGetServiceCentersQuery } from "../../../services/serviceCenter";

const ServiceMan = ({ companyId }: { companyId?: string }) => {
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const [serviceManForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [editId, setEditId] = useState("");
    const [getRoles, getRolesResult] = useLazyGetRolesQuery();
    const [getServiceCenters, getServiceCentersResult] = useLazyGetServiceCentersQuery();
    const [getServiceMen, getServiceMenResult] = useLazyGetServiceMenQuery();
    const [createServiceMan, createServiceManResult] = useCreateServiceManMutation();
    const [updateServiceMan, updateServiceManResult] = useUpdateServiceManMutation();
    const [deleteServiceMan, deleteServiceManResult] = useDeleteServiceManMutation();

    useEffect(() => {
        getServiceMen({ companyId: companyId || loggedInUser?.currentCompany?.id }).unwrap();
        getRoles({}).unwrap();
        getServiceCenters({ companyId: companyId || loggedInUser?.currentCompany?.id }).unwrap();
    }, [companyId, loggedInUser?.currentCompany?.id]);

    const showModal = () => {
        setMode(Actions.CREATE);
        setOpen(true);
    };

    const hideModal = () => {
        setOpen(false);
        serviceManForm.resetFields();
        setMode("");
    };

    const onSave = async () => {
        try {
            await serviceManForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = serviceManForm.getFieldsValue();

        if (mode === Actions.CREATE) {
            try {
                await createServiceMan({
                    companyId: companyId || loggedInUser?.currentCompany?.id,
                    body: formFields
                }).unwrap();
                setOpen(false);
                serviceManForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateServiceMan({
                    companyId: companyId || loggedInUser?.currentCompany?.id,
                    userId: editId,
                    body: formFields
                }).unwrap();
                setOpen(false);
                serviceManForm.resetFields();
            } catch (e) { }
        }
    };

    const handleEdit = (record: any) => {
        setMode(Actions.EDIT);
        setOpen(true);
        if (record.id) setEditId(record.id);
        serviceManForm.setFieldsValue(record);
    };

    const handleDelete = async (record: any) => {
        try {
            await deleteServiceMan({
                companyId: loggedInUser?.currentCompany?.id,
                userId: record.id
            }).unwrap();
        } catch (e) { }
    };

    const serviceMenColumn = (roles: any[]): any[] => [
        {
            key: "name",
            header: "Name",
            className: "text-primary font-medium",
            cell: Cells.USER_AVATAR_NAME
        },
        { key: "loginName", header: "Login Name", responsive: ["sm", "md", "lg", "xl", "xxl"], className: "font-medium" },
        { key: "email", header: "Email", responsive: ["sm", "md", "lg", "xl", "xxl"], className: "font-medium" },
        { key: "mobileNo", header: "Mobile No.", responsive: ["sm", "md", "lg", "xl", "xxl"], className: "font-medium" },
        { key: "roleId", header: "Role", className: "font-medium", fn: (value: any) => roles.find((x: any) => x.id === value)?.name },
        { key: "isActive", header: "Active?", className: "font-medium", fn: (value: any) => (value ? "Yes" : "No") },
    ];

    return (
        <>
            <div className="flex justify-between items-center gap-3 pb-1">
                <div className="text-headline">{"Service Men"}</div>
                <Button type="primary" className="flex-end" onClick={showModal}>
                    <PlusOutlined /> Add
                </Button>
            </div>
            <div>
                <Table
                    rowKey="id"
                    showHeader={true}
                    columns={tableUtils.buildColumns(
                        getServiceMenResult.isSuccess ? getServiceMenResult.data : [],
                        serviceMenColumn(getRolesResult.isSuccess ? getRolesResult.data : []),
                        tableUtils.editDeleteActionColumns(handleEdit, handleDelete)
                    )}
                    dataSource={getServiceMenResult.data || []}
                    size="middle"
                    className="ant_table"
                    loading={{
                        spinning: (getServiceMenResult.isFetching || getServiceMenResult.isLoading) as boolean,
                        indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                    }}
                    pagination={false}
                />
            </div>
            <div>
                <HrnModal
                    open={open}
                    title={`${mode === Actions.CREATE ? "Add" : "Edit"} Service Man`}
                    width={520}
                    isLoading={
                        (createServiceManResult.isLoading ||
                            updateServiceManResult.isLoading) as boolean
                    }
                    onCancel={hideModal}
                    onSave={onSave}
                >
                    <div className="mt-6">
                        <Form
                            form={serviceManForm}
                            layout="vertical"
                            name="serviceManForm"
                            validateMessages={validateMessages}
                        >
                            <ServiceManForm
                                mode={mode}
                                form={serviceManForm}
                                roles={getRolesResult.isSuccess ? getRolesResult.data : []}
                                serviceCenters={getServiceCentersResult.isSuccess ? getServiceCentersResult.data : []}
                            />
                        </Form>
                    </div>
                </HrnModal>
            </div>
        </>
    );
};

export default ServiceMan;